import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

// styles
import './style.scss';

// layout components
import Preloader from 'components/Preloader';
import ErrorBoundary from 'engine/ErrorBoundary';

// assets
import { LoadingOutlined } from '@ant-design/icons';

// components
import UserAvatar from 'components/UserAvatar';

// global user
import { withUser, useChildNotificatoin, useGetChildNotification, useUpdateChildNotifications } from 'engine/Contexts/User.context';

// utils
import api, { setAuthorizationHeader } from 'utils/apis/axiosAPI';
import { getObjId } from 'utils/helpers/helper.general';

// hooks
import { useMounted } from 'utils/hooks';

const ChildPreview = ({
  view,
  loading,
  isActive,
  child,
  user,
  // actions
  onMount,
  onClick,
  // default props
  className,
  style,
}) => {
  const mainClassNames = classNames('child-preview', 'child', className);

  const location = useLocation();
  const mounted = useMounted();
  const getChildNotification = useGetChildNotification();
  const updateChildNotifications = useUpdateChildNotifications();

  const activeChildRef = useRef(null);

  const [loadNotif, setLoadNotif] = useState(false);
  const [currentBadge, setCurrentBadge] = useState(false);

  // Added force reference for active item, when component is mounted
  useEffect(() => {
    if (!!isActive && !!onMount) {
      onMount(activeChildRef);
    }
  }, [isActive]);
  
  useEffect(() => {
    setLoadNotif(true);
    if (!!child) {
      mountNotification(child);
    }
  }, []);

  useEffect(() => {
    if (!!mounted && isActive) {
      mountNotification(child);
    }
  }, [isActive, location.pathname]);

  const mountNotification = async (child) => {
    const result = await getChildNotification(child?._id);
    setCurrentBadge(result?.amount);
    !!result && setLoadNotif(false);
    updateChildNotifications({
      _id: child?._id,
      ...result
    });
  }

  const onClickHandler = () => {
    !!onClick && !loading && onClick();
  }

  return !!child && (
    <div
      className={classNames(
        mainClassNames,
        loading && 'loading',
        isActive && "active"
      )}
      onClick={onClickHandler}
      style={style}
      ref={isActive ? activeChildRef : null}
    >
      {loading && (
        <Preloader className="overlay-preloader" size="small" />
      )}
      {child?._id && view == 'full' && (
        <div className="child-preview-wrapper">
          {!!currentBadge && (<span className="badge">{currentBadge}</span>)}
          {!!loadNotif && (<LoadingOutlined className="badge-preloader" />)}
          <UserAvatar fontSize={22} width={54} height={54} user={child} />
          <div className="child-preview__info">
            {child?.description?.firstname &&
              child?.description?.lastname && (
                <span className="info-name">
                  {child?.description?.firstname}{' '}
                  {child?.description?.lastname}
                </span>
              )}
          </div>
        </div>
      )}
      {/* // compact view of student info (for header) */}
      {child?._id && view == 'small' && (
        <>
          <div className="child-preview-small-wrapper">
            {!!currentBadge && (<span className="badge">{currentBadge}</span>)}
            {!!loadNotif && (<LoadingOutlined className="badge-preloader" />)}
            <UserAvatar view='small' fontSize={16} width={54} height={54} user={child} />
          </div>
        </>
      )}
    </div>
  );

}

ChildPreview.defaultProps = {
  view: 'full',
  loading: false,
  badge: false
};
ChildPreview.propTypes = {
  view: PropTypes.oneOf(['full', 'small']).isRequired,
  child: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.bool,
    PropTypes.string,
  ]),
  onClick: PropTypes.func,
};

export default compose(
  withUser
)(ChildPreview);
